import Graph from 'graphology';
import { useEffect, useRef } from 'react';
import Sigma from 'sigma';

import { PERFORMANCE_CONFIG } from '@/components/nodeGraph/config/graphConfig';

/**
 * useGraphRenderLoop - Graph Rendering Loop Management Hook
 *
 * This hook manages the continuous rendering loop for the graph visualization,
 * providing smooth animations and performance optimization.
 *
 * Features:
 * - Configurable FPS for rendering performance
 * - Automatic animation frame management
 * - Cleanup on unmount
 * - Performance optimization with frame rate limiting
 *
 * @param graphRef - Reference to the graph instance
 * @param fps - Frames per second for rendering (default from PERFORMANCE_CONFIG)
 */
export const useGraphRenderLoop = (
  sigma: Sigma,
  graphRef: Graph | null,
  fps: number = PERFORMANCE_CONFIG.targetFPS
) => {
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!sigma || !graphRef) return;

    let lastRefresh = 0;
    const frameDuration = 1000 / fps;

    const renderLoop = (now: number) => {
      if (sigma.getGraph().order > 0) {
        if (now - lastRefresh > frameDuration) {
          sigma.refresh();
          lastRefresh = now;
        }
      }
      animationFrameRef.current = requestAnimationFrame(renderLoop);
    };

    // Start the render loop immediately
    animationFrameRef.current = requestAnimationFrame(renderLoop);

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [sigma, graphRef, fps]);

  return { animationFrameRef };
};
